import React, { useEffect, useState } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Check, X, Minus, Gavel, Loader2, Sparkles, ShieldQuestion } from "lucide-react";

const verdictMeta = {
  correct: { icon: Check, cls: "bg-emerald-50 text-emerald-700 border-emerald-200", label: "Correct" },
  partial: { icon: Check, cls: "bg-amber-50 text-amber-700 border-amber-200", label: "Partial" },
  wrong: { icon: X, cls: "bg-rose-50 text-rose-700 border-rose-200", label: "Wrong" },
  unattempted: { icon: Minus, cls: "bg-stone-100 text-stone-600 border-stone-200", label: "Skipped" },
};

const arbiterVerdict = {
  ai_correct: { label: "AI was right", cls: "bg-stone-100 text-stone-700" },
  user_correct: { label: "You were right — re-graded", cls: "bg-emerald-50 text-emerald-700 border border-emerald-300" },
  both_valid: { label: "Both valid", cls: "bg-amber-50 text-amber-700" },
  ambiguous: { label: "Ambiguous", cls: "bg-stone-100 text-stone-700" },
};

export default function ReviewAnswers({ attemptId }) {
  const [items, setItems] = useState(null);
  const [filter, setFilter] = useState("all"); // all | wrong | challenged
  const [challengeQ, setChallengeQ] = useState(null);

  const load = async () => {
    try {
      const r = await api.get(`/attempts/${attemptId}/review`);
      setItems(r.data.items || []);
    } catch (err) {
      console.error("review load failed:", err);
    }
  };

  useEffect(() => { load(); }, [attemptId]);

  if (!items) {
    return <div className="text-sm text-stone-500">Loading review…</div>;
  }

  const filtered = items.filter((it) => {
    if (filter === "wrong") return it.verdict === "wrong" || it.verdict === "partial";
    if (filter === "challenged") return !!it.challenge;
    return true;
  });

  return (
    <div className="space-y-4">
      <Card className="p-4 rounded-md border-stone-200 shadow-none flex items-center gap-3">
        <ShieldQuestion className="h-5 w-5 text-orange-600" />
        <div className="flex-1 text-sm text-stone-700">
          Disagree with the AI answer? Click <strong>Challenge</strong>. Claude Sonnet 4.5 arbitrates — if you're right, your score updates automatically.
        </div>
        <div className="flex gap-1">
          {[
            { v: "all", l: `All (${items.length})` },
            { v: "wrong", l: `Wrong (${items.filter((i) => i.verdict === "wrong" || i.verdict === "partial").length})` },
            { v: "challenged", l: `Challenged (${items.filter((i) => !!i.challenge).length})` },
          ].map((f) => (
            <Button
              key={f.v}
              variant={filter === f.v ? "default" : "outline"}
              size="sm"
              onClick={() => setFilter(f.v)}
              className={filter === f.v ? "bg-orange-600 hover:bg-orange-700" : ""}
              data-testid={`filter-${f.v}`}
            >
              {f.l}
            </Button>
          ))}
        </div>
      </Card>

      {filtered.length === 0 ? (
        <Card className="p-16 rounded-md border-stone-200 shadow-none text-center">
          <p className="text-sm text-stone-500">Nothing to show for this filter.</p>
        </Card>
      ) : (
        <div className="space-y-3" data-testid="review-list">
          {filtered.map((it) => {
            const m = verdictMeta[it.verdict];
            const V = m.icon;
            const ch = it.challenge;
            return (
              <Card key={it.q_id} className="p-5 rounded-md border-stone-200 shadow-none">
                <div className="flex items-start gap-4">
                  <div className={`flex-shrink-0 h-10 w-10 rounded-md border flex items-center justify-center ${m.cls}`}>
                    <V className="h-5 w-5" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-2 flex-wrap">
                      <span className="font-mono text-xs text-stone-500">Q{it.number}</span>
                      <Badge variant="outline" className="font-mono text-[10px] uppercase tracking-[0.15em]">
                        {it.q_type}
                      </Badge>
                      <Badge variant="outline" className="font-mono text-[10px] uppercase tracking-[0.15em]">
                        {it.section}
                      </Badge>
                      <Badge className={`${m.cls} border font-mono text-[10px] uppercase tracking-[0.15em]`}>
                        {m.label}
                      </Badge>
                    </div>
                    <p className="text-sm text-stone-900 leading-snug mb-3">{it.text}</p>

                    {(it.q_type === "mcq" || it.q_type === "mcq_multi") && it.options.length > 0 && (
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-1.5 mb-3">
                        {it.options.map((opt, i) => {
                          const letter = String.fromCharCode(65 + i);
                          const parseSet = (v) => new Set(String(v || "").toUpperCase().replace(/\s/g, "").split(",").filter(Boolean));
                          const studentSet = it.q_type === "mcq_multi" ? parseSet(it.student_answer) : new Set(it.student_answer ? [String(it.student_answer).toUpperCase()] : []);
                          const aiSet = it.q_type === "mcq_multi" ? parseSet(it.ai_answer) : new Set(it.ai_answer ? [String(it.ai_answer).toUpperCase()] : []);
                          const isStudent = studentSet.has(letter);
                          const isAi = aiSet.has(letter);
                          return (
                            <div
                              key={`${it.q_id}-opt-${letter}`}
                              className={`text-xs px-3 py-2 rounded-md border flex items-center gap-2 ${
                                isStudent && isAi
                                  ? "border-emerald-400 bg-emerald-50"
                                  : isStudent
                                  ? "border-rose-400 bg-rose-50"
                                  : isAi
                                  ? "border-emerald-400 bg-emerald-50"
                                  : "border-stone-200"
                              }`}
                            >
                              <span className="font-mono font-semibold w-4">{letter}.</span>
                              <span className="flex-1 truncate">{opt}</span>
                              {isStudent && (
                                <Badge variant="secondary" className="font-mono text-[9px]">You</Badge>
                              )}
                              {isAi && (
                                <Badge variant="secondary" className="font-mono text-[9px] bg-emerald-100 text-emerald-700">Key</Badge>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    )}

                    {it.q_type !== "mcq" && it.q_type !== "mcq_multi" && (
                      <div className="grid grid-cols-2 gap-3 mb-3 text-sm">
                        <div className="border rounded-md p-3 border-stone-200">
                          <div className="font-mono text-[10px] uppercase tracking-[0.15em] text-stone-500 mb-1">Your answer</div>
                          <div className="font-mono font-semibold">{it.student_answer || <span className="text-stone-400">—</span>}</div>
                        </div>
                        <div className="border rounded-md p-3 border-emerald-200 bg-emerald-50">
                          <div className="font-mono text-[10px] uppercase tracking-[0.15em] text-emerald-700 mb-1">AI answer key</div>
                          <div className="font-mono font-semibold text-emerald-800">{it.ai_answer || <span className="text-stone-400">not extracted</span>}</div>
                        </div>
                      </div>
                    )}

                    {ch ? (
                      <div className={`mt-3 p-3 rounded-md ${arbiterVerdict[ch.arbiter_verdict]?.cls || "bg-stone-100"}`}>
                        <div className="flex items-center gap-2 mb-1">
                          <Sparkles className="h-3.5 w-3.5" />
                          <span className="font-mono text-[10px] uppercase tracking-[0.15em] font-semibold">
                            {arbiterVerdict[ch.arbiter_verdict]?.label || ch.arbiter_verdict}
                          </span>
                          {ch.arbiter_confidence && (
                            <span className="font-mono text-[10px] text-stone-500">
                              · confidence: {ch.arbiter_confidence}
                            </span>
                          )}
                        </div>
                        <div className="text-xs mb-1">
                          <span className="font-semibold">Your answer:</span> {ch.user_answer}
                          {ch.arbiter_correct_answer && (
                            <>
                              {" · "}
                              <span className="font-semibold">Arbiter says:</span> {ch.arbiter_correct_answer}
                            </>
                          )}
                        </div>
                        <div className="text-xs text-stone-700 leading-relaxed">{ch.arbiter_explanation}</div>
                      </div>
                    ) : (
                      it.verdict !== "unattempted" && (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => setChallengeQ(it)}
                          className="border-orange-500 text-orange-600 hover:bg-orange-50 hover:text-orange-700 mt-1"
                          data-testid={`challenge-${it.q_id}`}
                        >
                          <Gavel className="h-3.5 w-3.5 mr-1" />
                          Challenge this answer
                        </Button>
                      )
                    )}
                  </div>
                </div>
              </Card>
            );
          })}
        </div>
      )}

      <ChallengeDialog
        item={challengeQ}
        onClose={() => setChallengeQ(null)}
        onDone={() => {
          setChallengeQ(null);
          load();
        }}
        attemptId={attemptId}
      />
    </div>
  );
}

function ChallengeDialog({ item, onClose, onDone, attemptId }) {
  const [ans, setAns] = useState("");
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (item) {
      setAns(item.student_answer || "");
      setReason("");
    }
  }, [item]);

  const submit = async () => {
    if (!ans.trim()) {
      toast.error("Enter your proposed answer");
      return;
    }
    setBusy(true);
    try {
      const r = await api.post(`/attempts/${attemptId}/challenge`, {
        q_id: item.q_id,
        user_answer: ans.trim(),
        reason: reason.trim() || null,
      });
      const v = r.data.challenge?.arbiter_verdict;
      if (v === "user_correct") toast.success("Arbiter agrees — your score has been updated!");
      else if (v === "both_valid") toast.info("Arbiter says both answers are valid.");
      else toast.info("Arbiter has ruled on your challenge.");
      onDone();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Challenge failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={!!item} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="sm:max-w-lg" data-testid="challenge-dialog">
        {item && (
          <>
            <DialogHeader>
              <div className="flex items-center gap-2 mb-1">
                <Gavel className="h-4 w-4 text-orange-600" />
                <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-stone-500">Challenge</span>
              </div>
              <DialogTitle className="font-heading text-xl font-bold tracking-tight">
                Q{item.number} · Dispute the AI's answer
              </DialogTitle>
              <DialogDescription className="text-sm">
                Claude Sonnet 4.5 (a stronger reasoning model) will arbitrate. If you're right, your score is updated automatically.
              </DialogDescription>
            </DialogHeader>
            <div className="border-l-2 border-stone-300 pl-3 py-1 text-sm text-stone-700 mb-2">
              {item.text}
            </div>
            <div className="text-xs text-stone-500 mb-3">
              AI says: <span className="font-mono font-semibold text-stone-900">{item.ai_answer || "—"}</span>
            </div>
            <div className="space-y-3">
              <div>
                <Label className="font-mono text-[10px] uppercase tracking-[0.2em] text-stone-500">
                  Your proposed answer
                </Label>
                <Input
                  value={ans}
                  onChange={(e) => setAns(e.target.value)}
                  placeholder={item.q_type === "mcq" ? "A / B / C / D" : item.q_type === "mcq_multi" ? "e.g. A,C" : "Your answer"}
                  data-testid="challenge-answer-input"
                />
              </div>
              <div>
                <Label className="font-mono text-[10px] uppercase tracking-[0.2em] text-stone-500">
                  Reasoning (optional but recommended)
                </Label>
                <textarea
                  value={reason}
                  onChange={(e) => setReason(e.target.value)}
                  rows={3}
                  placeholder="Why do you think your answer is correct?"
                  className="mt-1 w-full rounded-md border border-stone-300 bg-white px-3 py-2 text-sm focus:border-orange-500 focus:outline-none focus:ring-2 focus:ring-orange-500/20"
                  data-testid="challenge-reason-input"
                />
              </div>
              <Button
                onClick={submit}
                disabled={busy || !ans.trim()}
                className="w-full bg-orange-600 hover:bg-orange-700 text-white h-11"
                data-testid="challenge-submit-btn"
              >
                {busy ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Gavel className="h-4 w-4 mr-2" />}
                Submit to arbiter
              </Button>
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
